import React, { useState, useMemo } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useGroupLeaderPermission } from '@/components/hooks/useGroupLeaderPermission';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import { motion } from 'framer-motion';
import { Users, Plus, Edit, Trash2, Search, Baby } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function IntegrantesKidsMBV() {
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    email: '',
    roles: [],
    member_id: '',
    member_name: ''
  });

  const queryClient = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me()
  });

  const { data: permissions } = useQuery({
    queryKey: ['adminPermissions', user?.email],
    queryFn: async () => {
      if (!user?.email) return null;
      const byUserEmail = await base44.entities.AdminPermission.filter({ user_email: user.email });
      if (byUserEmail.length > 0) return byUserEmail[0];
      const byEmail = await base44.entities.AdminPermission.filter({ email: user.email });
      return byEmail[0] || null;
    },
    enabled: !!user?.email
  });

  const { canManageKids } = useGroupLeaderPermission();

  const { data: members = [], isLoading } = useQuery({
    queryKey: ['kidsMembers'],
    queryFn: () => base44.entities.KidsMBVMember.list('-created_date')
  });

  const { data: churchMembers = [] } = useQuery({
    queryKey: ['churchMembers'],
    queryFn: () => base44.entities.Member.filter({ status: 'Ativo' })
  });

  const sortedChurchMembers = useMemo(
    () => [...churchMembers].sort((a, b) =>
      String(a.full_name || '').localeCompare(String(b.full_name || ''), 'pt-BR', { sensitivity: 'base' })
    ),
    [churchMembers]
  );

  const isAdmin = user?.role === 'admin' || permissions?.is_super_admin;
  const canManage = canManageKids;

  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.KidsMBVMember.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kidsMembers'] });
      toast.success('Integrante cadastrado com sucesso!');
      resetForm();
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.KidsMBVMember.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kidsMembers'] });
      toast.success('Integrante atualizado com sucesso!');
      resetForm();
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.KidsMBVMember.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kidsMembers'] });
      toast.success('Integrante excluído com sucesso!');
    }
  });

  const roleOptions = ['Professor', 'Ajudante', 'Ministração do Louvor', 'Teatro'];

  const resetForm = () => {
    setFormData({
      full_name: '',
      phone: '',
      email: '',
      roles: [],
      member_id: '',
      member_name: ''
    });
    setEditingMember(null);
    setShowForm(false);
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setFormData({
      full_name: member.full_name || '',
      phone: member.phone || '',
      email: member.email || '',
      roles: member.roles || [],
      member_id: member.member_id || '',
      member_name: member.member_name || ''
    });
    setShowForm(true);
  };

  const handleSubmit = () => {
    if (!formData.full_name) {
      toast.error('O nome é obrigatório.');
      return;
    }

    if (editingMember) {
      updateMutation.mutate({ id: editingMember.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const toggleRole = (role) => {
    const updated = formData.roles.includes(role)
      ? formData.roles.filter(r => r !== role)
      : [...formData.roles, role];
    setFormData({ ...formData, roles: updated });
  };

  const filteredMembers = members.filter(member =>
    member.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    member.email?.toLowerCase().includes(search.toLowerCase())
  );

  const churchMemberPhotoById = useMemo(() => {
    return new Map((churchMembers || []).map((m) => [m.id, m.photo_url]));
  }, [churchMembers]);
  const selectedChurchMember = useMemo(
    () => churchMembers.find((m) => m.id === formData.member_id),
    [churchMembers, formData.member_id]
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-gradient-to-br from-pink-600 to-purple-700 text-white pt-8 pb-6 px-4">
        <div className="max-w-lg mx-auto">
          <PageHeader 
            title="Integrantes" 
            subtitle={`${members.length} na equipe`}
            showBack={true}
            action={
              canManage && (
                <Button onClick={() => setShowForm(true)} size="sm" className="bg-white text-pink-700 hover:bg-pink-50">
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar
                </Button>
              )
            }
          />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Buscar integrante"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>

        {filteredMembers.length === 0 ? (
          <EmptyState
            icon={Users}
            title="Nenhum integrante encontrado"
            description="Comece adicionando membros à equipe"
            actionLabel={canManage ? "Adicionar Integrante" : undefined}
            onAction={canManage ? () => setShowForm(true) : undefined}
          />
        ) : (
          <div className="space-y-3">
            {filteredMembers.map((member, index) => (
              <motion.div
                key={member.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                className="bg-white rounded-xl p-4 shadow-sm"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    {churchMemberPhotoById.get(member.member_id) ? (
                      <img
                        src={churchMemberPhotoById.get(member.member_id)}
                        alt={member.full_name}
                        className="w-11 h-11 rounded-full object-cover border border-gray-200 flex-shrink-0"
                      />
                    ) : (
                      <div className="w-11 h-11 rounded-full bg-pink-100 text-pink-700 border border-pink-200 flex items-center justify-center font-semibold flex-shrink-0">
                        {member.full_name?.charAt(0)?.toUpperCase() || 'I'}
                      </div>
                    )}
                    <div className="min-w-0">
                      <h3 className="font-semibold text-gray-900">{member.full_name}</h3>
                    </div>
                  </div>
                  {canManage && (
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" onClick={() => handleEdit(member)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        className="text-red-600"
                        onClick={() => {
                          if (confirm('Deseja excluir este integrante?')) {
                            deleteMutation.mutate(member.id);
                          }
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>

                {member.roles?.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <Baby className="w-4 h-4 text-pink-500" />
                    {member.roles.map((role, i) => (
                      <Badge key={i} variant="secondary" className="bg-pink-50 text-pink-700">
                        {role}
                      </Badge>
                    ))}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{editingMember ? 'Editar Integrante' : 'Novo Integrante'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label>Nome Completo *</Label>
              <Input
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Telefone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              <div>
                <Label>E-mail</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label className="mb-3 block">Funções</Label>
              <div className="space-y-2">
                {roleOptions.map(role => (
                  <div key={role} className="flex items-center gap-2">
                    <Checkbox
                      checked={formData.roles.includes(role)}
                      onCheckedChange={() => toggleRole(role)}
                    />
                    <label className="text-sm cursor-pointer" onClick={() => toggleRole(role)}>
                      {role}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label>Vincular ao Membro</Label>
              <Select 
                value={formData.member_id} 
                onValueChange={(value) => {
                  const selectedMember = churchMembers.find(m => m.id === value);
                  setFormData({ 
                    ...formData, 
                    member_id: value,
                    member_name: selectedMember?.full_name || '',
                    full_name: selectedMember?.full_name || formData.full_name,
                    phone: selectedMember?.phone || formData.phone,
                    email: selectedMember?.email || formData.email
                  });
                }}
              >
                <SelectTrigger>
                  {selectedChurchMember ? (
                    <div className="flex items-center gap-2">
                      {selectedChurchMember.photo_url ? (
                        <img
                          src={selectedChurchMember.photo_url}
                          alt={selectedChurchMember.full_name}
                          className="w-6 h-6 rounded-full object-cover border border-gray-200"
                        />
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-pink-100 text-pink-700 border border-pink-200 flex items-center justify-center text-xs font-semibold">
                          {selectedChurchMember.full_name?.charAt(0)?.toUpperCase() || 'M'}
                        </div>
                      )}
                      <span className="truncate">{selectedChurchMember.full_name}</span>
                    </div>
                  ) : (
                    <SelectValue placeholder="Selecione um membro" />
                  )}
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={null}>Nenhum</SelectItem>
                  {sortedChurchMembers.map(member => (
                    <SelectItem key={member.id} value={member.id}>
                      <div className="flex items-center gap-2">
                        {member.photo_url ? (
                          <img
                            src={member.photo_url}
                            alt={member.full_name}
                            className="w-6 h-6 rounded-full object-cover border border-gray-200"
                          />
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-pink-100 text-pink-700 border border-pink-200 flex items-center justify-center text-xs font-semibold">
                            {member.full_name?.charAt(0)?.toUpperCase() || 'M'}
                          </div>
                        )}
                        <span>{member.full_name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" className="flex-1" onClick={resetForm}>
                Cancelar
              </Button>
              <Button 
                className="flex-1" 
                onClick={handleSubmit}
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {editingMember ? 'Salvar' : 'Cadastrar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
